import { logger } from '../logger.js';
const DEFAULT_TICK_LOOP_CONFIG = {
    statsReportIntervalMs: 100,
    circuitBreakerThreshold: 3,
    circuitBreakerCooldownMs: 1000,
};
export class TickLoop {
    config;
    callbacks;
    engineTick = null;
    // Scheduling
    running = false;
    rafId = null;
    // Timing
    lastFrameTime = 0;
    lastStatsReport = 0;
    frameCount = 0;
    // FPS smoothing (500ms window)
    fpsAcc = 0;
    fpsFrames = 0;
    currentFps = 0;
    // Circuit breaker
    cbFailures = 0;
    cbOpen = false;
    cbLastFailure = 0;
    constructor(callbacks, config = {}) {
        this.callbacks = callbacks;
        this.config = { ...DEFAULT_TICK_LOOP_CONFIG, ...config };
    }
    setEngine(engine) {
        this.engineTick = engine;
    }
    getEngine() {
        return this.engineTick;
    }
    setEngineFactory(factory) {
        this.callbacks.engineFactory = factory;
    }
    start() {
        this.running = true;
        this.lastFrameTime = performance.now();
        this.lastStatsReport = performance.now();
        this.tick();
    }
    stop() {
        this.running = false;
        if (this.rafId !== null)
            cancelAnimationFrame(this.rafId);
        this.rafId = null;
    }
    get isRunning() {
        return this.running;
    }
    get isCircuitOpen() {
        return this.cbOpen;
    }
    // --- Core tick ---
    tick = () => {
        if (!this.running)
            return;
        const now = performance.now();
        const dt = now - this.lastFrameTime;
        this.lastFrameTime = now;
        // FPS smoothing
        this.fpsAcc += dt;
        this.fpsFrames++;
        if (this.fpsAcc >= 500) {
            this.currentFps = Math.round((this.fpsFrames * 1000) / this.fpsAcc);
            this.fpsAcc = 0;
            this.fpsFrames = 0;
        }
        // Circuit breaker: if open, skip engine_tick and just render last good frame
        if (this.cbOpen) {
            if (now - this.cbLastFailure > this.config.circuitBreakerCooldownMs) {
                this.attemptRecovery();
            }
            this.callbacks.render();
            this.rafId = requestAnimationFrame(this.tick);
            return;
        }
        // Build tick context
        const ctx = this.callbacks.buildContext(dt);
        // Engine tick — wrapped in try/catch for circuit breaker recovery
        let tickMs = 0;
        let commandsDrained = 0;
        const t0 = performance.now();
        try {
            if (this.engineTick)
                this.engineTick(ctx);
            tickMs = performance.now() - t0;
            commandsDrained = ctx.commandsDrained ?? 0;
            // Success — reset failure counter
            this.cbFailures = 0;
        }
        catch (err) {
            tickMs = performance.now() - t0;
            this.cbFailures++;
            this.cbLastFailure = now;
            const message = err instanceof Error ? err.message : String(err);
            const threshold = this.config.circuitBreakerThreshold;
            logger.error(`[CircuitBreaker] engine_tick failed (${this.cbFailures}/${threshold}): ${message}`);
            if (this.cbFailures >= threshold) {
                this.cbOpen = true;
                this.callbacks.onError(-1, `Engine crashed ${threshold} times consecutively. ` +
                    `Last error: ${message}. Rendering frozen at last good frame. ` +
                    `Auto-recovery in ${this.config.circuitBreakerCooldownMs}ms.`);
            }
            else {
                this.callbacks.onError(-1, `engine_tick trap: ${message}`);
            }
        }
        // Check error region — engine may have written an error code into the SAB
        if (this.callbacks.checkErrorRegion) {
            const errInfo = this.callbacks.checkErrorRegion();
            if (errInfo) {
                this.callbacks.onError(errInfo.code, errInfo.message);
            }
        }
        // Render (always — even after a trap, show the last good frame)
        this.callbacks.render();
        // Report stats (time-based, configurable interval)
        this.frameCount++;
        if (now - this.lastStatsReport >= this.config.statsReportIntervalMs) {
            this.lastStatsReport = now;
            this.callbacks.onStats({
                frameCount: this.frameCount,
                fps: this.currentFps,
                tickMs: Math.round(tickMs * 100) / 100,
                commandsDrained,
            });
        }
        this.rafId = requestAnimationFrame(this.tick);
    };
    // --- Circuit breaker recovery ---
    attemptRecovery() {
        logger.log('[CircuitBreaker] Attempting engine recovery...');
        if (this.callbacks.engineFactory) {
            try {
                this.engineTick = this.callbacks.engineFactory();
                logger.log('[CircuitBreaker] Engine re-instantiated successfully');
            }
            catch (err) {
                logger.error('[CircuitBreaker] Engine re-instantiation failed:', err);
                this.cbLastFailure = performance.now();
                return;
            }
        }
        this.cbOpen = false;
        this.cbFailures = 0;
    }
}
//# sourceMappingURL=tick-loop.js.map